import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Milestone, MilestoneStartDecision } from '../types';
import { effectiveTimelineDeliveryStatus, timelineDeliveryPresentation } from '../utils/timelineDeliveryPresentation';

export type MilestoneStartSelectorContext = 'signing' | 'attestation' | 'manual';

interface MilestoneStartSelectorProps {
  milestones: Milestone[];
  userRole: 'company' | 'person';
  context: MilestoneStartSelectorContext;
  attestingMilestoneId?: string;
  disabled?: boolean;
  onDecisionsChange: (decisions: MilestoneStartDecision[] | undefined) => void;
}

const isJointlyAttested = (milestone: Milestone) =>
  milestone.companyAttestation === 'approved' &&
  milestone.personAttestation === 'approved';

const isStarted = (milestone: Milestone) =>
  Boolean(milestone.workStartedAt) ||
  Boolean(milestone.deliveryStatus && milestone.deliveryStatus !== 'not_started');

export default function MilestoneStartSelector({
  milestones,
  userRole,
  context,
  attestingMilestoneId,
  disabled = false,
  onDecisionsChange
}: MilestoneStartSelectorProps) {
  const { t } = useTranslation();
  const planMilestones = useMemo(
    () => [...milestones]
      .filter(milestone => (milestone.itemType ?? 'milestone') === 'milestone')
      // Completed work is history, not a start decision. This also covers
      // carried-over payouts whose attestation fields have been reset.
      .filter(milestone => !isJointlyAttested(milestone) && !milestone.paidAt &&
        !['paid', 'jointly_attested', 'payout_processing', 'payout_failed'].includes(milestone.deliveryStatus ?? ''))
      .filter(milestone => context !== 'attestation' || milestone.id !== attestingMilestoneId)
      .sort((left, right) => left.milestoneNumber - right.milestoneNumber),
    [milestones, context, attestingMilestoneId]
  );
  const initial = useMemo(() => {
    const selected = new Set(
      planMilestones.filter(isStarted).map(milestone => milestone.id)
    );
    const hasActiveStage = planMilestones.some(
      milestone => isStarted(milestone) && !isJointlyAttested(milestone)
    );
    const suggested = new Set<string>();
    if (context !== 'manual' && !hasActiveStage) {
      const candidate = planMilestones.find(milestone =>
        !isJointlyAttested(milestone) &&
        !milestone.companyStartDecision
      );
      if (candidate) {
        selected.add(candidate.id);
        suggested.add(candidate.id);
      }
    }
    return { selected, suggested };
  }, [context, planMilestones]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(initial.selected);

  useEffect(() => {
    setSelectedIds(initial.selected);
    onDecisionsChange(undefined);
  }, [initial, onDecisionsChange]);

  const canChangeStart = (milestone: Milestone) => {
    if (!isStarted(milestone)) {
      return userRole === 'company' || !milestone.companyStartDecision;
    }
    return userRole === 'company' &&
      milestone.workStartedByRole === 'person' &&
      milestone.companyAttestation === 'not_attested' &&
      milestone.personAttestation === 'not_attested';
  };
  const canToggle = (milestone: Milestone) => !disabled && canChangeStart(milestone);

  const emit = (nextSelected: Set<string>) => {
    // Returning to the initial selection is not an explicit start decision.
    const changedIds = planMilestones
      .filter(milestone => nextSelected.has(milestone.id) !== initial.selected.has(milestone.id))
      .map(milestone => milestone.id);
    if (changedIds.length === 0) {
      onDecisionsChange(undefined);
      return;
    }
    const ids = new Set([...changedIds, ...initial.suggested]);
    const decisions = planMilestones
      .filter(milestone => ids.has(milestone.id))
      .map(milestone => ({
        milestoneId: milestone.id,
        started: nextSelected.has(milestone.id)
      }));
    onDecisionsChange(decisions);
  };

  const toggle = (milestone: Milestone) => {
    if (!canToggle(milestone)) return;
    const nextSelected = new Set(selectedIds);
    if (nextSelected.has(milestone.id)) nextSelected.delete(milestone.id);
    else nextSelected.add(milestone.id);
    setSelectedIds(nextSelected);
    emit(nextSelected);
  };

  if (planMilestones.length === 0) return null;
  if (context === 'attestation' && !planMilestones.some(canChangeStart)) return null;

  return (
    <section className="border rounded-3 p-3 bg-white" data-testid="milestone-start-selector">
      <div className="d-flex align-items-start gap-2 mb-3">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{ width: 34, height: 34, background: '#eaf2ff', color: '#3454d1' }}
        >
          <i className="feather-play" aria-hidden="true"></i>
        </span>
        <div>
          <div className="fw-semibold">
            {context === 'attestation'
              ? t('milestoneStarts.selectorAttestationTitle')
              : planMilestones.length === 1
              ? t('milestoneStarts.selectorSingleTitle')
              : t('milestoneStarts.selectorTitle')}
          </div>
          <div className="small text-muted">
            {context === 'attestation'
              ? t('milestoneStarts.selectorAttestationHint')
              : context === 'manual'
              ? t('milestoneStarts.selectorManualHint')
              : t('milestoneStarts.selectorFlowHint')}
          </div>
        </div>
      </div>

      <div className="d-grid gap-2">
        {planMilestones.map(milestone => {
          const checked = selectedIds.has(milestone.id);
          const toggleAllowed = canToggle(milestone);
          const customerStart = isStarted(milestone) && milestone.workStartedByRole === 'person';
          const presentation = timelineDeliveryPresentation(
            effectiveTimelineDeliveryStatus(milestone) ?? 'in_progress', t
          );
          return (
            <label
              key={milestone.id}
              className={`d-flex gap-3 align-items-start border rounded-3 p-3 ${
                toggleAllowed ? 'cursor-pointer' : 'bg-light'
              }`}
              style={{ cursor: toggleAllowed ? 'pointer' : 'default' }}
            >
              <input
                className="form-check-input mt-1"
                type="checkbox"
                checked={checked}
                disabled={!toggleAllowed}
                onChange={() => toggle(milestone)}
                aria-label={t('milestoneStarts.stageAria', { number: milestone.milestoneNumber })}
              />
              <span className="flex-grow-1">
                <span className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="fw-semibold">
                    {t('milestoneStarts.stageNumber', { number: milestone.milestoneNumber })}
                  </span>
                  {checked && isStarted(milestone) && (
                    <span className={`badge ${presentation.badgeClass}`}>{presentation.label}</span>
                  )}
                  {customerStart && (
                    <span className="badge bg-info text-dark">
                      {t('milestoneStarts.customerMarked')}
                    </span>
                  )}
                </span>
                <span className="small text-muted d-block mt-1">{milestone.description}</span>
                {userRole === 'company' && customerStart && toggleAllowed && (
                  <span className="small text-warning-emphasis d-block mt-1">
                    <i className="feather-info me-1" aria-hidden="true"></i>
                    {t('milestoneStarts.companyOverrideHint')}
                  </span>
                )}
                {userRole === 'person' && milestone.companyStartDecision && !isStarted(milestone) && (
                  <span className="small text-muted d-block mt-1">
                    {t('milestoneStarts.companyOwnsDecision')}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}
